import { useEffect, useState } from "react";
import {
  ArrowLeft,
  Shield,
  Smartphone,
  Lock,
  IndianRupee,
  ChevronDown,
  Calendar,
  Package,
  Users,
  MapPin,
  Hash,
  Layers,
} from "lucide-react";

import { useLocation } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../../../config/api";

// ─── MOCK DATA ────────────────────────────────────────────────────────────────
const mockPayoutRow = {
  id: "PAY-9001",
  forType: "MCP",
  entityName: "Rahul Patil",
  period: "Week 42 · 2025",
  role: "Warranty Sales Payout",
  commissionAmount: 22000,
  payableAmount: 20000,
  transferred: true,
  bankName: "HDFC Bank",
  accountLast4: "1234",
  txnId: "TXN-HDFC-2025-001",
  date: "2025-10-25",
};

const PRODUCTS = ["Extended Warranty", "Screen Damage", "Total Protection"];

const mcpRetailers = [
  {
    retailerId: "RET-001",
    retailerName: "Shree Mobile Store",
    city: "Pune",
    warranties: {
      "Extended Warranty": { units: 18, rate: 350, commission: 6300 },
      "Screen Damage":     { units: 12, rate: 220, commission: 2640 },
      "Total Protection":  { units: 9,  rate: 480, commission: 4320 },
    },
  },
  {
    retailerId: "RET-002",
    retailerName: "Galaxy Electronics",
    city: "Nashik",
    warranties: {
      "Extended Warranty": { units: 10, rate: 350, commission: 3500 },
      "Screen Damage":     { units: 20, rate: 220, commission: 4400 },
      "Total Protection":  { units: 5,  rate: 480, commission: 2400 },
    },
  },
  {
    retailerId: "RET-003",
    retailerName: "TechZone Retail",
    city: "Aurangabad",
    warranties: {
      "Extended Warranty": { units: 14, rate: 350, commission: 4900 },
      "Screen Damage":     { units: 8,  rate: 220, commission: 1760 },
      "Total Protection":  { units: 11, rate: 480, commission: 5280 },
    },
  },
];

const cpRetailers = [
  {
    retailerId: "RET-101",
    retailerName: "Priya Electronics",
    city: "Mumbai",
    warranties: {
      "Extended Warranty": { units: 22, rate: 350, commission: 7700 },
      "Screen Damage":     { units: 15, rate: 220, commission: 3300 },
      "Total Protection":  { units: 7,  rate: 480, commission: 3360 },
    },
  },
  {
    retailerId: "RET-102",
    retailerName: "CityMart Mobile",
    city: "Thane",
    warranties: {
      "Extended Warranty": { units: 8,  rate: 350, commission: 2800 },
      "Screen Damage":     { units: 17, rate: 220, commission: 3740 },
      "Total Protection":  { units: 13, rate: 480, commission: 6240 },
    },
  },
];

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  navy:        "#0B1426",
  navyMid:     "#132040",
  navyBorder:  "#1E3058",
  navyText:    "#8BA3C4",
  navyMuted:   "#4A6080",
  white:       "#FFFFFF",
  offWhite:    "#F7F9FC",
  border:      "#E8EDF5",
  borderMid:   "#D0D9E8",
  textPrimary: "#0D1B2E",
  textSecond:  "#4A5A72",
  textMuted:   "#8B98AB",
  ew:  { base: "#1A56DB", light: "#EBF3FF", border: "#C3DAFF", mono: "#0E3A9A" },
  sd:  { base: "#6D28D9", light: "#F3EEFF", border: "#D9C8FF", mono: "#40187D" },
  tp:  { base: "#047857", light: "#EAFAF4", border: "#BBEDDA", mono: "#024D38" },
  success: { bg: "#F0FDF8", text: "#047857", border: "#BBEDDA", dot: "#10B981" },
  pending: { bg: "#FFFBEB", text: "#B45309", border: "#FDE68A", dot: "#F59E0B" },
};

const productToken = (p) =>
  p === "Extended Warranty" ? T.ew : p === "Screen Damage" ? T.sd : T.tp;
const productIcon = (p) =>
  p === "Extended Warranty" ? Shield : p === "Screen Damage" ? Smartphone : Lock;

const fmtFull = (n) => n.toLocaleString("en-IN");
const mono = { fontFamily: "'IBM Plex Mono', 'Courier New', monospace" };
const sans = { fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif" };

// ─────────────────────────────────────────────────────────────────────────────
//  ATOMS
// ─────────────────────────────────────────────────────────────────────────────
function StatusBadge({ transferred }) {
  const s = transferred ? T.success : T.pending;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 10px", borderRadius:100, fontSize:11, fontWeight:700, background:s.bg, color:s.text, border:`1px solid ${s.border}` }}>
      <span style={{ width:6, height:6, borderRadius:"50%", background:s.dot, display:"inline-block" }} />
      {transferred ? "Transferred" : "Pending"}
    </span>
  );
}

function MetaTag({ icon: Icon, children }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, color:T.navyText }}>
      <Icon size={12} color={T.navyMuted} />{children}
    </span>
  );
}

function SectionHeading({ children, right }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
      <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.10em", textTransform:"uppercase", color:T.textMuted }}>
        {children}
      </span>
      {right}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PAGE HEADER — dark navy band
// ─────────────────────────────────────────────────────────────────────────────
function PageHeader({ payout, onBack }) {
  return (
    <div style={{ background:T.navy, borderRadius:16, padding:"24px 28px", display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:20 }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:16 }}>
        {onBack && (
          <button onClick={onBack} style={{ width:36, height:36, borderRadius:10, background:T.navyMid, border:`1px solid ${T.navyBorder}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0, marginTop:2 }}>
            <ArrowLeft size={14} color={T.navyText} />
          </button>
        )}
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
            <span style={{ fontSize:10, fontWeight:800, letterSpacing:"0.10em", textTransform:"uppercase", color:T.navyText }}>Commissions</span>
            <span style={{ color:T.navyBorder }}>/</span>
            <span style={{ fontSize:10, fontWeight:700, color:T.navyText, ...mono }}>{payout.id}</span>
            <StatusBadge transferred={payout.transferred} />
          </div>
          <h1 style={{ fontSize:22, fontWeight:800, color:T.white, lineHeight:1.15, margin:0, ...sans }}>{payout.entityName}</h1>
          <div style={{ display:"flex", alignItems:"center", gap:20, marginTop:10, flexWrap:"wrap" }}>
            <MetaTag icon={Calendar}>{payout.period}</MetaTag>
            <MetaTag icon={Package}>{payout.role}</MetaTag>
          </div>
        </div>
      </div>

      <div style={{ background:T.navyMid, border:`1px solid ${T.navyBorder}`, borderRadius:12, padding:"16px 22px", textAlign:"right", flexShrink:0, minWidth:190 }}>
        <p style={{ fontSize:10, fontWeight:700, letterSpacing:"0.10em", textTransform:"uppercase", color:T.navyText, margin:"0 0 6px" }}>Payable Amount</p>
        <p style={{ fontSize:26, fontWeight:700, color:T.white, lineHeight:1, margin:0, ...mono }}>₹{fmtFull(payout.payableAmount)}</p>
        <div style={{ marginTop:8, paddingTop:8, borderTop:`1px solid ${T.navyBorder}` }}>
          <p style={{ fontSize:11, color:T.navyText, margin:0 }}>{payout.bankName} · ••••{payout.accountLast4}</p>
          <p style={{ fontSize:10, color:T.navyMuted, marginTop:2, ...mono }}>{payout.txnId}</p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TAB BAR — underline style
// ─────────────────────────────────────────────────────────────────────────────
function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display:"flex", borderBottom:`1px solid ${T.border}` }}>
      {tabs.map((t) => {
        const on = active === t.key;
        return (
          <button key={t.key} onClick={() => onChange(t.key)}
            style={{ display:"flex", alignItems:"center", gap:8, padding:"13px 24px", fontSize:13, fontWeight:700, background:"transparent", color:on ? T.textPrimary : T.textMuted, border:"none", borderBottom:`2px solid ${on ? T.navy : "transparent"}`, marginBottom:-1, cursor:"pointer", transition:"all 0.15s", ...sans }}>
            <Users size={13} />
            {t.label}
            <span style={{ display:"inline-flex", alignItems:"center", justifyContent:"center", width:20, height:20, borderRadius:5, fontSize:10, fontWeight:800, background:on ? T.navy : T.border, color:on ? T.white : T.textMuted, ...mono }}>
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  KPI STRIP — 4 metric cards
// ─────────────────────────────────────────────────────────────────────────────
function KpiStrip({ productData = [], summary = {} }) {

    // 🔹 Always keep these 3 products (UI requirement)
  const DEFAULT_PRODUCTS = [
    "Extended Warranty",
    "Screen Damage",
    "Total Protection",
  ];

  // 🔹 Convert API data to map
  const productMap = {};
  (productData || []).forEach((item) => {
    productMap[item.product_name] = {
      commission: Number(item.commission || 0),
      units: Number(item.units || 0),
    };
  });

  // 🔹 Build final product list (include missing as 0)
  const byProduct = DEFAULT_PRODUCTS.map((p) => ({
    product: p,
    commission: productMap[p]?.commission || 0,
    units: productMap[p]?.units || 0,
  }));

  // 🔹 Summary values
  const totalComm = Number(summary?.total_commission || 0);
  const totalUnits = Number(summary?.total_units || 0);

  const cards = [
    { label:"Total Commission", value:`₹${fmtFull(totalComm)}`, sub:`${totalUnits} warranties`, accent:T.navy, bg:T.offWhite, border:T.border, textC:T.textPrimary, icon:IndianRupee },
    ...byProduct.map((bp) => {
      const tk = productToken(bp.product);
      return { label:bp.product, value:`₹${fmtFull(bp.commission)}`, sub:`${bp.units} units`, accent:tk.base, bg:tk.light, border:tk.border, textC:tk.mono, icon:productIcon(bp.product) };
    }),
  ];

  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} style={{ background:c.bg, border:`1px solid ${c.border}`, borderRadius:12, padding:"16px 18px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
              <span style={{ fontSize:10, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", color:T.textMuted, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:"70%" }}>
                {c.label}
              </span>
              <div style={{ width:28, height:28, borderRadius:8, background:`${c.accent}18`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <Icon size={13} color={c.accent} />
              </div>
            </div>
            <p style={{ fontSize:19, fontWeight:700, color:c.textC, margin:0, lineHeight:1, ...mono }}>{c.value}</p>
            <p style={{ fontSize:11, color:T.textMuted, marginTop:5 }}>{c.sub}</p>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  PRODUCT TABLE
// ─────────────────────────────────────────────────────────────────────────────
function ProductTable({ retailers, productData }) {
  const rows = (productData || []).map((item) => {
    return {
      p: item.product_name,
      totalUnits: Number(item.units || 0),
      totalComm: Number(item.commission || 0),
      rate: Number(item.rate || 0),
      pct: Number(item.contribution || 0), // already % from API
    };
  });

  const grandComm = rows.reduce((s, r) => s + r.totalComm, 0);
  const grandUnits = rows.reduce((s, r) => s + r.totalUnits, 0);
  const th = { padding:"10px 16px", textAlign:"left", fontSize:10, fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", color:T.textMuted, background:T.offWhite, borderBottom:`1px solid ${T.border}` };
  const td = { padding:"13px 16px", fontSize:13 };

  return (
    <div style={{ border:`1px solid ${T.border}`, borderRadius:12, overflow:"hidden" }}>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr>
            <th style={th}>Product</th>
            <th style={{ ...th, textAlign:"right" }}>Rate / Unit</th>
            <th style={{ ...th, textAlign:"right" }}>Units Sold</th>
            <th style={{ ...th, textAlign:"right" }}>Commission</th>
            <th style={{ ...th, textAlign:"right" }}>Contribution</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ p, totalUnits, totalComm, rate, pct }, i) => {
            const tk = productToken(p);
            const Icon = productIcon(p);
            return (
              <tr key={p} style={{ borderBottom: i < rows.length-1 ? `1px solid ${T.border}` : "none", background:T.white }}>
                <td style={td}>
                  <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{ width:30, height:30, borderRadius:8, background:tk.light, border:`1px solid ${tk.border}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                      <Icon size={13} color={tk.base} />
                    </div>
                    <span style={{ fontWeight:600, color:T.textPrimary }}>{p}</span>
                  </div>
                </td>
                <td style={{ ...td, textAlign:"right", color:T.textSecond, ...mono }}>₹{rate}</td>
                <td style={{ ...td, textAlign:"right" }}>
                  <span style={{ display:"inline-block", padding:"2px 10px", borderRadius:6, fontSize:12, fontWeight:700, background:tk.light, color:tk.mono, border:`1px solid ${tk.border}`, ...mono }}>{totalUnits}</span>
                </td>
                <td style={{ ...td, textAlign:"right", fontWeight:700, color:tk.mono, ...mono }}>₹{fmtFull(totalComm)}</td>
                <td style={{ ...td, textAlign:"right" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, justifyContent:"flex-end" }}>
                    <div style={{ width:56, height:4, background:T.border, borderRadius:100, overflow:"hidden" }}>
                      <div style={{ width:`${pct}%`, height:"100%", background:tk.base, borderRadius:100 }} />
                    </div>
                    <span style={{ fontSize:11, fontWeight:700, color:T.textMuted, minWidth:28, textAlign:"right", ...mono }}>{pct}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ background:T.offWhite, borderTop:`2px solid ${T.borderMid}` }}>
            <td style={{ ...td, fontWeight:800, fontSize:10, letterSpacing:"0.08em", textTransform:"uppercase", color:T.textMuted }} colSpan={2}>Grand Total</td>
            <td style={{ ...td, textAlign:"right" }}><span style={{ fontSize:13, fontWeight:700, color:T.textPrimary, ...mono }}>{grandUnits}</span></td>
            <td style={{ ...td, textAlign:"right" }}><span style={{ fontSize:14, fontWeight:700, color:T.textPrimary, ...mono }}>₹{fmtFull(grandComm)}</span></td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  FILTER BAR
// ─────────────────────────────────────────────────────────────────────────────
function FilterBar({ active, onChange }) {
  const options = [
    { key:"ALL", label:"All Products", icon:Layers, accent:T.navy, bg:T.offWhite, border:T.borderMid, textC:T.textSecond },
    ...PRODUCTS.map((p) => {
      const tk = productToken(p);
      const shortLabel = p === "Extended Warranty" ? "Ext. Warranty" : p === "Screen Damage" ? "Screen Damage" : "Total Protect.";
      return { key:p, label:shortLabel, icon:productIcon(p), accent:tk.base, bg:tk.light, border:tk.border, textC:tk.mono };
    }),
  ];
  return (
    <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
      {options.map(({ key, label, icon:Icon, accent, bg, border, textC }) => {
        const on = active === key;
        return (
          <button key={key} onClick={() => onChange(key)}
            style={{ display:"flex", alignItems:"center", gap:6, padding:"6px 13px", borderRadius:7, fontSize:12, fontWeight:700, background:on ? accent : bg, color:on ? T.white : textC, border:`1px solid ${on ? accent : border}`, cursor:"pointer", transition:"all 0.12s", ...sans }}>
            <Icon size={12} />{label}
          </button>
        );
      })}
    </div>
  );
}

function RetailerRow({ retailer, productFilter, index, payoutId }) {

  const [warrantyData, setWarrantyData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const filteredProducts = productFilter === "ALL" ? PRODUCTS : [productFilter];
  
  // Calculate totals for the header
  const totalComm = filteredProducts.reduce((s, p) => s + retailer.warranties[p].commission, 0);
  const totalUnits = filteredProducts.reduce((s, p) => s + retailer.warranties[p].units, 0);
  const pct = Math.min(100, Math.round((totalComm / 15000) * 100));
  const avatarTk = [T.ew, T.sd, T.tp][index % 3];


  const fetchWarranties = async (pageNo = 1) => {
  try {
    setLoading(true);

    const res = await axios.get(
      `${BASE_URL}warranty/payout-warranties`,
      {
        params: {
          payout_id: payoutId,
          entity_id: retailer.retailerId, // cp_id already mapped
          per_page: 10,
          page: pageNo,
        },
      }
    );

    setWarrantyData(res?.data?.data?.data || []);
  } catch (err) {
    console.error("Warranty API error", err);
  } finally {
    setLoading(false);
  }
};


  const thStyle = { padding: "10px 12px", textAlign: "left", fontSize: 10, fontWeight: 800, color: T.textMuted, borderBottom: `1px solid ${T.border}`, textTransform: "uppercase" };
  const tdStyle = { padding: "10px 12px", fontSize: 12, color: T.textPrimary, borderBottom: `1px solid ${T.offWhite}` };

  return (
    <div style={{ background: T.white, border: `1px solid ${open ? T.borderMid : T.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 8 }}>
      {/* Header (Click to Expand) */}
      <div onClick={() => { setOpen(!open); 
            if (!open) fetchWarranties();
           }} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 20px", cursor: "pointer" }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: avatarTk.light, border: `1px solid ${avatarTk.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: avatarTk.mono, ...mono }}>
          {retailer.retailerName.slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: "0 0 160px" }}>
          <p style={{ margin: 0, fontSize: 13, fontWeight: 700 }}>{retailer.retailerName}</p>
          <span style={{ fontSize: 11, color: T.textMuted }}>{retailer.city} • {retailer.retailerId}</span>
        </div>
        <div style={{ flex: 1, textAlign: "right", paddingRight: 20 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: T.navy, ...mono }}>₹{fmtFull(totalComm)}</p>
          <p style={{ margin: 0, fontSize: 11, color: T.textMuted }}>{totalUnits} Units Total</p>
        </div>
        <ChevronDown size={14} color={T.textMuted} style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "0.2s" }} />
      </div>

      {/* Expanded Detail View */}
      {open && (
        <div style={{ padding: "0 20px 20px", background: "#FCFDFF" }}>
          <div style={{ marginTop: 10, border: `1px solid ${T.border}`, borderRadius: 8, background: T.white, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: T.offWhite }}>
                  <th style={thStyle}>Warranty ID</th>
                  <th style={thStyle}>Product Type</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Date</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Payout</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center" }}>
                      Loading...
                    </td>
                  </tr>
                  ) : warrantyData.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center" }}>
                      No data
                    </td>
                  </tr>
                  ) : (
                  warrantyData.map((item) => {
                    const tk = productToken(item.product_type);
                    return (
                      <tr key={item.id}>
                        <td style={{ ...tdStyle, ...mono, fontWeight: 600 }}>
                          {item.warranty_code}
                        </td>

                        <td style={tdStyle}>
                          <span
                            style={{
                              padding: "2px 8px",
                              borderRadius: 4,
                              background: tk.light,
                              color: tk.mono,
                              fontSize: 10,
                              fontWeight: 700,
                            }}
                          >
                            {item.product_type}
                          </span>
                        </td>

                        <td style={{ ...tdStyle, color: T.textMuted }}>
                          {item.product_name}
                        </td>

                        <td style={tdStyle}>
                          {new Date(item.date).toLocaleDateString("en-IN")}
                        </td>

                        <td
                          style={{
                            ...tdStyle,
                            textAlign: "right",
                            fontWeight: 700,
                            ...mono,
                          }}
                        >
                          ₹{fmtFull(item.payout_amount)}
                        </td>
                      </tr>
                    );
                  })
                  )}
              </tbody>
              <tfoot>
                <tr style={{ background: T.offWhite }}>
                  <td colSpan={4} style={{ ...tdStyle, fontWeight: 800, textAlign: "right", fontSize: 10 }}>Subtotal</td>
                  <td style={{ ...tdStyle, textAlign: "right", fontWeight: 800, color: T.navy, ...mono }}>₹{fmtFull(warrantyData.reduce((s, w) => s + Number(w.payout_amount || 0), 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  TAB CONTENT
// ─────────────────────────────────────────────────────────────────────────────
function TabContent({ retailers, label, productData, summaryData, row }) {
  const [filter, setFilter] = useState("ALL");
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:24 }}>
      <KpiStrip productData={productData} summary={summaryData} />

      <div>
        <SectionHeading>Product Breakdown — {label}</SectionHeading>
        <ProductTable retailers={retailers} productData={productData} />
      </div>

      <div>
        <SectionHeading right={<FilterBar active={filter} onChange={setFilter} />}>
          {label} Retailers ({retailers.length})
        </SectionHeading>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {retailers.map((r, i) => (
            <RetailerRow
              key={r.retailerId}
              retailer={r}
              productFilter={filter}
              index={i}
              payoutId={row?.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ROOT
// ─────────────────────────────────────────────────────────────────────────────
export default function CommissionDetailView({ onBack }) {
  const [activeTab, setActiveTab] = useState("MCP");

  const location = useLocation();
  const row = location.state?.row;

  const payout = row || mockPayoutRow;


  const [productData, setProductData] = useState([]);
  const [summaryData, setSummaryData] = useState({});
  const [loading, setLoading] = useState(false);

  const [cpApiData, setCpApiData] = useState([]);

  useEffect(() => {
  if (!row) return;

  const fetchProductWise = async () => {
    try {
      setLoading(true);

      const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

      let payout_id;
      let role;

      // 🔥 ROLE LOGIC
      if (user?.role === 1) {
        payout_id = row?.id || row?.id;
        role = row?.role;
      } else if (user?.role === 2 || user?.role === 4) {
        payout_id = row?.id;
        role = user?.role;
      }

      const res = await axios.get(
        `${BASE_URL}warranty/payouts/product-wise`,
        {
          params: {
            payout_id,
            role,
          },
        }
      );

      setProductData(res?.data?.products || []);
      setSummaryData(res?.data?.summary || {});

    } catch (err) {
      console.error("Product wise API error", err);
    } finally {
      setLoading(false);
    }
  };

  fetchProductWise();

    const fetchCpPayouts = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("authUserData") || "{}");

      let entity_id;
      let role;

      // 🔥 Role logic
      if (user?.role === 1) {
        entity_id = row?.entity_id || row?.id;
        role = row?.role;
      } else if (user?.role === 2) {
        entity_id = user?.id;
        role = user?.role;
      }

      const res = await axios.get(
        `${BASE_URL}warranty/payouts/cp-payouts`,
        {
          params: { entity_id, role },
        }
      );

      setCpApiData(res?.data?.data?.data || []);

    } catch (err) {
      console.error("CP payouts API error", err);
    }
  };
  fetchCpPayouts();
}, [row]);

  const tabs = [
    { key:"MCP", label:"MCP", count:mcpRetailers.length },
    { key:"CP",  label:"CP",  count:cpRetailers.length },
  ];

  const mapCpApiToRetailers = (apiData) => {
  return apiData.map((item) => {
    const totalUnits = Number(item.total_devices || 0);
    const totalPayout = Number(item.payout_amount || 0);

    const perUnitRate = totalUnits > 0 ? Math.floor(totalPayout / totalUnits) : 0;

    const baseUnits = Math.floor(totalUnits / 3);
    const remainder = totalUnits % 3;

    return {
      retailerId: item.cp_code || item.cp_id,
      retailerName: item.cp_name || "—",
      city: "—",

      warranties: {
        "Extended Warranty": {
          units: baseUnits + (remainder > 0 ? 1 : 0),
          rate: perUnitRate,
          commission: (baseUnits + (remainder > 0 ? 1 : 0)) * perUnitRate,
        },
        "Screen Damage": {
          units: baseUnits + (remainder > 1 ? 1 : 0),
          rate: perUnitRate,
          commission: (baseUnits + (remainder > 1 ? 1 : 0)) * perUnitRate,
        },
        "Total Protection": {
          units: baseUnits,
          rate: perUnitRate,
          commission: baseUnits * perUnitRate,
        },
      },
    };
  });
};

  const retailers =
  activeTab === "MCP"
    ? mapCpApiToRetailers(cpApiData) // ✅ API data
    : cpRetailers; 

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20, paddingBottom:40, maxWidth:"100%", ...sans }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');
        *{box-sizing:border-box;} button{cursor:pointer;} h1,p{margin:0;}
      `}</style>

      <PageHeader payout={payout} onBack={onBack} />

      <div style={{ background:T.white, border:`1px solid ${T.border}`, borderRadius:16, overflow:"hidden" }}>
        <div style={{ padding:"0 24px" }}>
          <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />
        </div>
        <div style={{ padding:"24px" }}>
          <TabContent key={activeTab} retailers={retailers} label={activeTab} productData={productData} summaryData={summaryData} row={row}/>
        </div>
      </div>
    </div>
  );
}

// import { useState, useMemo } from "react";
// import {
//   ArrowLeft,
//   Shield,
//   Smartphone,
//   Lock,
//   TrendingUp,
//   IndianRupee,
//   ChevronRight,
//   Award,
//   BarChart2,
//   Calendar,
//   Package,
//   Users,
//   CheckCircle2,
//   Clock,
// } from "lucide-react";

// // ─── MOCK DATA ────────────────────────────────────────────────────────────────
// const mockPayoutRow = {
//   id: "PAY-9001",
//   forType: "MCP",
//   entityName: "Rahul Patil",
//   period: "Week 42 · 2025",
//   role: "Warranty Sales Payout",
//   commissionAmount: 22000,
//   payableAmount: 20000,
//   transferred: true,
//   bankName: "HDFC Bank",
//   accountLast4: "1234",
//   txnId: "TXN-HDFC-2025-001",
//   date: "2025-10-25",
// };

// const PRODUCTS = ["Extended Warranty", "Screen Damage", "Total Protection"];

// const mcpRetailers = [
//   {
//     retailerId: "RET-001",
//     retailerName: "Shree Mobile Store",
//     city: "Pune",
//     warranties: {
//       "Extended Warranty": { units: 18, rate: 350, commission: 6300 },
//       "Screen Damage":     { units: 12, rate: 220, commission: 2640 },
//       "Total Protection":  { units: 9,  rate: 480, commission: 4320 },
//     },
//   },
//   {
//     retailerId: "RET-002",
//     retailerName: "Galaxy Electronics",
//     city: "Nashik",
//     warranties: {
//       "Extended Warranty": { units: 10, rate: 350, commission: 3500 },
//       "Screen Damage":     { units: 20, rate: 220, commission: 4400 },
//       "Total Protection":  { units: 5,  rate: 480, commission: 2400 },
//     },
//   },
//   {
//     retailerId: "RET-003",
//     retailerName: "TechZone Retail",
//     city: "Aurangabad",
//     warranties: {
//       "Extended Warranty": { units: 14, rate: 350, commission: 4900 },
//       "Screen Damage":     { units: 8,  rate: 220, commission: 1760 },
//       "Total Protection":  { units: 11, rate: 480, commission: 5280 },
//     },
//   },
// ];

// const cpRetailers = [
//   {
//     retailerId: "RET-101",
//     retailerName: "Priya Electronics",
//     city: "Mumbai",
//     warranties: {
//       "Extended Warranty": { units: 22, rate: 350, commission: 7700 },
//       "Screen Damage":     { units: 15, rate: 220, commission: 3300 },
//       "Total Protection":  { units: 7,  rate: 480, commission: 3360 },
//     },
//   },
//   {
//     retailerId: "RET-102",
//     retailerName: "CityMart Mobile",
//     city: "Thane",
//     warranties: {
//       "Extended Warranty": { units: 8,  rate: 350, commission: 2800 },
//       "Screen Damage":     { units: 17, rate: 220, commission: 3740 },
//       "Total Protection":  { units: 13, rate: 480, commission: 6240 },
//     },
//   },
// ];

// // ─── HELPERS ──────────────────────────────────────────────────────────────────
// const formatCurrency = (n) =>
//   n >= 100000
//     ? `${(n / 100000).toFixed(2)}L`
//     : n >= 1000
//     ? `${(n / 1000).toFixed(1)}K`
//     : String(n);

// const productMeta = {
//   "Extended Warranty": {
//     icon: Shield,
//     accent: "#2563EB",
//     bg: "#EFF6FF",
//     border: "#BFDBFE",
//     label: "EW",
//   },
//   "Screen Damage": {
//     icon: Smartphone,
//     accent: "#7C3AED",
//     bg: "#F5F3FF",
//     border: "#DDD6FE",
//     label: "SD",
//   },
//   "Total Protection": {
//     icon: Lock,
//     accent: "#059669",
//     bg: "#ECFDF5",
//     border: "#A7F3D0",
//     label: "TP",
//   },
// };

// // ─────────────────────────────────────────────────────────────────────────────
// //  ATOM: ProductPill
// // ─────────────────────────────────────────────────────────────────────────────
// function ProductPill({ product, active, onClick }) {
//   const m = productMeta[product];
//   const Icon = m.icon;
//   return (
//     <button
//       onClick={onClick}
//       className="flex items-center gap-2 px-4 py-2 rounded-xl text-[12px] font-bold transition-all duration-150 border"
//       style={{
//         background: active ? m.accent : "#F8FAFC",
//         color:      active ? "#fff"   : "#64748B",
//         borderColor: active ? m.accent : "#E2E8F0",
//         boxShadow: active ? `0 2px 8px ${m.accent}33` : "none",
//       }}
//     >
//       <Icon size={13} />
//       {product}
//     </button>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  MOLECULE: SummaryKpiBar
// // ─────────────────────────────────────────────────────────────────────────────
// function SummaryKpiBar({ retailers }) {
//   const totalCommission = retailers.reduce((s, r) =>
//     s + PRODUCTS.reduce((ps, p) => ps + r.warranties[p].commission, 0), 0);
//   const totalUnits = retailers.reduce((s, r) =>
//     s + PRODUCTS.reduce((ps, p) => ps + r.warranties[p].units, 0), 0);

//   const byProduct = PRODUCTS.map((p) => ({
//     product: p,
//     commission: retailers.reduce((s, r) => s + r.warranties[p].commission, 0),
//     units:      retailers.reduce((s, r) => s + r.warranties[p].units, 0),
//   }));

//   return (
//     <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
//       {/* Total */}
//       <div
//         className="rounded-2xl p-4 flex flex-col gap-1"
//         style={{ background: "#1E293B", gridColumn: "span 1" }}
//       >
//         <div className="flex items-center gap-2">
//           <div
//             className="w-7 h-7 rounded-lg flex items-center justify-center"
//             style={{ background: "#334155" }}
//           >
//             <IndianRupee size={13} color="#94A3B8" />
//           </div>
//           <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
//             Total Commission
//           </p>
//         </div>
//         <p
//           className="text-[22px] font-black leading-none mt-1"
//           style={{ color: "#F8FAFC", fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//         >
//           ₹{formatCurrency(totalCommission)}
//         </p>
//         <p className="text-[11px] text-slate-400">{totalUnits} warranties sold</p>
//       </div>

//       {/* Per product */}
//       {byProduct.map((bp) => {
//         const m = productMeta[bp.product];
//         const Icon = m.icon;
//         return (
//           <div
//             key={bp.product}
//             className="rounded-2xl p-4 flex flex-col gap-1 border"
//             style={{ background: m.bg, borderColor: m.border }}
//           >
//             <div className="flex items-center gap-2">
//               <div
//                 className="w-7 h-7 rounded-lg flex items-center justify-center"
//                 style={{ background: m.accent + "22" }}
//               >
//                 <Icon size={13} style={{ color: m.accent }} />
//               </div>
//               <p
//                 className="text-[11px] font-bold uppercase tracking-widest"
//                 style={{ color: m.accent }}
//               >
//                 {m.label}
//               </p>
//             </div>
//             <p
//               className="text-[20px] font-black leading-none mt-1"
//               style={{ color: m.accent, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//             >
//               ₹{formatCurrency(bp.commission)}
//             </p>
//             <p className="text-[11px]" style={{ color: m.accent + "99" }}>
//               {bp.units} units
//             </p>
//           </div>
//         );
//       })}
//     </div>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  MOLECULE: RetailerCard  — expandable row
// // ─────────────────────────────────────────────────────────────────────────────
// function RetailerCard({ retailer, productFilter, index }) {
//   const [expanded, setExpanded] = useState(false);

//   const filteredProducts = productFilter === "ALL"
//     ? PRODUCTS
//     : [productFilter];

//   const totalForRetailer = filteredProducts.reduce(
//     (s, p) => s + retailer.warranties[p].commission, 0
//   );
//   const totalUnits = filteredProducts.reduce(
//     (s, p) => s + retailer.warranties[p].units, 0
//   );

//   // progress bar share (max 15000 = 100%)
//   const MAX_COMMISSION = 15000;
//   const pct = Math.min(100, Math.round((totalForRetailer / MAX_COMMISSION) * 100));

//   return (
//     <div
//       className="rounded-2xl border overflow-hidden transition-all duration-200"
//       style={{
//         background:   "#FFFFFF",
//         borderColor:  expanded ? "#CBD5E1" : "#F1F5F9",
//         boxShadow:    expanded ? "0 4px 20px rgba(0,0,0,0.08)" : "0 1px 4px rgba(0,0,0,0.04)",
//         animationDelay: `${index * 0.05}s`,
//       }}
//     >
//       {/* Header row */}
//       <div
//         className="flex items-center gap-3 px-5 py-4 cursor-pointer select-none"
//         onClick={() => setExpanded(!expanded)}
//       >
//         {/* Avatar */}
//         <div
//           className="w-9 h-9 rounded-xl flex items-center justify-center text-[12px] font-black flex-shrink-0"
//           style={{ background: "#EFF6FF", color: "#2563EB" }}
//         >
//           {retailer.retailerName.slice(0, 2).toUpperCase()}
//         </div>

//         {/* Name + city */}
//         <div className="flex-1 min-w-0">
//           <p className="text-[13px] font-bold text-slate-800 truncate">
//             {retailer.retailerName}
//           </p>
//           <p className="text-[11px] text-slate-400">{retailer.city} · {retailer.retailerId}</p>
//         </div>

//         {/* Progress bar + amount */}
//         <div className="flex-1 min-w-0 hidden sm:block">
//           <div
//             className="h-1.5 rounded-full overflow-hidden"
//             style={{ background: "#F1F5F9" }}
//           >
//             <div
//               className="h-full rounded-full transition-all duration-500"
//               style={{ width: `${pct}%`, background: "linear-gradient(90deg,#2563EB,#7C3AED)" }}
//             />
//           </div>
//           <p className="text-[10px] text-slate-400 mt-1">{pct}% of target</p>
//         </div>

//         {/* Commission + Units */}
//         <div className="text-right flex-shrink-0">
//           <p
//             className="text-[15px] font-black"
//             style={{ color: "#1E293B", fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//           >
//             ₹{formatCurrency(totalForRetailer)}
//           </p>
//           <p className="text-[10px] text-slate-400">{totalUnits} units</p>
//         </div>

//         {/* Chevron */}
//         <ChevronRight
//           size={14}
//           className="flex-shrink-0 transition-transform duration-200"
//           style={{
//             color: "#94A3B8",
//             transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
//           }}
//         />
//       </div>

//       {/* Expanded product breakdown */}
//       {expanded && (
//         <div
//           className="px-5 pb-4"
//           style={{ borderTop: "1px solid #F1F5F9" }}
//         >
//           <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3">
//             {filteredProducts.map((p) => {
//               const m = productMeta[p];
//               const Icon = m.icon;
//               const w = retailer.warranties[p];
//               return (
//                 <div
//                   key={p}
//                   className="rounded-xl p-3 border flex gap-3 items-start"
//                   style={{ background: m.bg, borderColor: m.border }}
//                 >
//                   <div
//                     className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
//                     style={{ background: m.accent + "22" }}
//                   >
//                     <Icon size={14} style={{ color: m.accent }} />
//                   </div>
//                   <div>
//                     <p className="text-[11px] font-bold" style={{ color: m.accent }}>
//                       {p}
//                     </p>
//                     <p
//                       className="text-[16px] font-black leading-tight mt-0.5"
//                       style={{ color: m.accent, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//                     >
//                       ₹{w.commission.toLocaleString("en-IN")}
//                     </p>
//                     <div className="flex gap-3 mt-1">
//                       <span className="text-[10px]" style={{ color: m.accent + "99" }}>
//                         {w.units} units
//                       </span>
//                       <span className="text-[10px]" style={{ color: m.accent + "99" }}>
//                         ₹{w.rate}/unit
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>

//           {/* Row total */}
//           <div
//             className="flex justify-between items-center mt-3 pt-3 rounded-xl px-4 py-2"
//             style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
//           >
//             <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">
//               Retailer Total
//             </p>
//             <p
//               className="text-[14px] font-black"
//               style={{ color: "#1E293B", fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//             >
//               ₹{totalForRetailer.toLocaleString("en-IN")}
//               <span className="text-[11px] font-semibold text-slate-400 ml-2">
//                 ({totalUnits} units)
//               </span>
//             </p>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  MOLECULE: ProductSummaryTable
// // ─────────────────────────────────────────────────────────────────────────────
// function ProductSummaryTable({ retailers }) {
//   const rows = PRODUCTS.map((p) => {
//     const totalUnits      = retailers.reduce((s, r) => s + r.warranties[p].units, 0);
//     const totalCommission = retailers.reduce((s, r) => s + r.warranties[p].commission, 0);
//     const rate            = retailers[0]?.warranties[p].rate ?? 0;
//     return { product: p, totalUnits, totalCommission, rate };
//   });

//   const grandTotal = rows.reduce((s, r) => s + r.totalCommission, 0);

//   return (
//     <div className="overflow-x-auto rounded-2xl border" style={{ borderColor: "#F1F5F9" }}>
//       <table className="min-w-full text-xs">
//         <thead>
//           <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #F1F5F9" }}>
//             {["Product", "Rate/Unit", "Units Sold", "Commission"].map((h) => (
//               <th
//                 key={h}
//                 className="px-5 py-3 text-left font-black uppercase tracking-widest"
//                 style={{ fontSize: 10, color: "#94A3B8" }}
//               >
//                 {h}
//               </th>
//             ))}
//           </tr>
//         </thead>
//         <tbody>
//           {rows.map((row) => {
//             const m = productMeta[row.product];
//             const Icon = m.icon;
//             return (
//               <tr
//                 key={row.product}
//                 className="hover:bg-slate-50 transition-colors"
//                 style={{ borderBottom: "1px solid #F8FAFC" }}
//               >
//                 <td className="px-5 py-3">
//                   <div className="flex items-center gap-2">
//                     <div
//                       className="w-7 h-7 rounded-lg flex items-center justify-center"
//                       style={{ background: m.bg }}
//                     >
//                       <Icon size={13} style={{ color: m.accent }} />
//                     </div>
//                     <span className="font-bold text-slate-700">{row.product}</span>
//                   </div>
//                 </td>
//                 <td className="px-5 py-3 font-semibold text-slate-600">
//                   ₹{row.rate}
//                 </td>
//                 <td className="px-5 py-3">
//                   <span
//                     className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-[11px] font-bold"
//                     style={{ background: m.bg, color: m.accent }}
//                   >
//                     {row.totalUnits}
//                   </span>
//                 </td>
//                 <td className="px-5 py-3">
//                   <span
//                     className="font-black text-[13px]"
//                     style={{ color: m.accent, fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//                   >
//                     ₹{row.totalCommission.toLocaleString("en-IN")}
//                   </span>
//                 </td>
//               </tr>
//             );
//           })}
//           {/* Grand total row */}
//           <tr style={{ background: "#F8FAFC", borderTop: "2px solid #E2E8F0" }}>
//             <td colSpan={2} className="px-5 py-3">
//               <span className="font-black text-slate-700 uppercase tracking-widest text-[10px]">
//                 Grand Total
//               </span>
//             </td>
//             <td className="px-5 py-3 font-black text-slate-700">
//               {rows.reduce((s, r) => s + r.totalUnits, 0)}
//             </td>
//             <td className="px-5 py-3">
//               <span
//                 className="font-black text-[14px]"
//                 style={{ color: "#1E293B", fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//               >
//                 ₹{grandTotal.toLocaleString("en-IN")}
//               </span>
//             </td>
//           </tr>
//         </tbody>
//       </table>
//     </div>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  TAB content
// // ─────────────────────────────────────────────────────────────────────────────
// function TabContent({ retailers, label }) {
//   const [productFilter, setProductFilter] = useState("ALL");

//   const filteredRetailers = retailers;

//   return (
//     <div className="space-y-5">
//       {/* KPI bar */}
//       <SummaryKpiBar retailers={retailers} />

//       {/* Product summary table */}
//       <div>
//         <p className="text-[11px] font-black uppercase tracking-widest text-slate-400 mb-3">
//           Product Breakdown — {label}
//         </p>
//         <ProductSummaryTable retailers={retailers} />
//       </div>

//       {/* Retailer cards */}
//       <div>
//         <div className="flex items-center justify-between mb-3">
//           <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">
//             {label} Retailers ({retailers.length})
//           </p>
//           {/* Product filter */}
//           <div className="flex gap-2 flex-wrap justify-end">
//             <button
//               onClick={() => setProductFilter("ALL")}
//               className="px-3 py-1.5 rounded-xl text-[11px] font-bold border transition-all"
//               style={{
//                 background:  productFilter === "ALL" ? "#1E293B" : "#F8FAFC",
//                 color:       productFilter === "ALL" ? "#F8FAFC"  : "#64748B",
//                 borderColor: productFilter === "ALL" ? "#1E293B"  : "#E2E8F0",
//               }}
//             >
//               All Products
//             </button>
//             {PRODUCTS.map((p) => (
//               <ProductPill
//                 key={p}
//                 product={p}
//                 active={productFilter === p}
//                 onClick={() => setProductFilter(productFilter === p ? "ALL" : p)}
//               />
//             ))}
//           </div>
//         </div>

//         <div className="space-y-3">
//           {filteredRetailers.map((r, i) => (
//             <RetailerCard
//               key={r.retailerId}
//               retailer={r}
//               productFilter={productFilter}
//               index={i}
//             />
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// }

// // ─────────────────────────────────────────────────────────────────────────────
// //  ROOT: CommissionDetailView
// // ─────────────────────────────────────────────────────────────────────────────
// export default function CommissionDetailView({ payout = mockPayoutRow, onBack }) {
//   const [activeTab, setActiveTab] = useState("MCP");

//   const tabs = [
//     { key: "MCP", label: "MCP", count: mcpRetailers.length, accent: "#2563EB" },
//     { key: "CP",  label: "CP",  count: cpRetailers.length,  accent: "#7C3AED" },
//   ];

//   const activeRetailers = activeTab === "MCP" ? mcpRetailers : cpRetailers;

//   return (
//     <div className="space-y-5 pb-10 bg-slate-50">

//       {/* ── BREADCRUMB HEADER ── */}
//       <div
//         className="flex items-start justify-between gap-4 rounded-2xl p-5"
//         style={{ background: "#F8FAFC", border: "1px solid #E2E8F0" }}
//       >
//         <div className="flex items-start gap-4">
//           {onBack && (
//             <button
//               onClick={onBack}
//               className="w-9 h-9 flex items-center justify-center rounded-xl border transition-all hover:bg-white"
//               style={{ borderColor: "#E2E8F0", background: "#fff" }}
//             >
//               <ArrowLeft size={15} color="#475569" />
//             </button>
//           )}
//           <div>
//             <div className="flex items-center gap-2 mb-1">
//               <span
//                 className="text-[11px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full"
//                 style={{ background: "#EFF6FF", color: "#2563EB" }}
//               >
//                 Commission Detail
//               </span>
//               <span className="text-slate-300">/</span>
//               <span className="text-[11px] font-bold text-slate-500">{payout.id}</span>
//             </div>
//             <h1
//               className="text-[20px] font-black text-slate-800"
//               style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//             >
//               {payout.entityName}
//             </h1>
//             <div className="flex items-center gap-3 mt-1 flex-wrap">
//               <span className="text-[12px] text-slate-500 flex items-center gap-1">
//                 <Calendar size={11} /> {payout.period}
//               </span>
//               <span className="text-[12px] text-slate-500 flex items-center gap-1">
//                 <Package size={11} /> {payout.role}
//               </span>
//               <span
//                 className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold"
//                 style={{
//                   background: payout.transferred ? "#ECFDF5" : "#FFFBEB",
//                   color:      payout.transferred ? "#059669" : "#D97706",
//                   border:     `1px solid ${payout.transferred ? "#A7F3D0" : "#FDE68A"}`,
//                 }}
//               >
//                 <span
//                   className="w-1.5 h-1.5 rounded-full"
//                   style={{ background: payout.transferred ? "#10B981" : "#F59E0B" }}
//                 />
//                 {payout.transferred ? "Transferred" : "Pending"}
//               </span>
//             </div>
//           </div>
//         </div>

//         {/* Total payout badge */}
//         <div
//           className="rounded-2xl px-5 py-4 text-right flex-shrink-0"
//           style={{ background: "#1E293B" }}
//         >
//           <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
//             Payable Amount
//           </p>
//           <p
//             className="text-[22px] font-black text-white leading-tight"
//             style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}
//           >
//             ₹{payout.payableAmount.toLocaleString("en-IN")}
//           </p>
//           <p className="text-[11px] text-slate-400 mt-0.5">
//             {payout.bankName} · ****{payout.accountLast4}
//           </p>
//         </div>
//       </div>

//       {/* ── TAB BAR ── */}
//       <div
//         className="flex items-center gap-1 p-1 rounded-2xl"
//         style={{ background: "#F1F5F9", width: "fit-content" }}
//       >
//         {tabs.map((t) => (
//           <button
//             key={t.key}
//             onClick={() => setActiveTab(t.key)}
//             className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-[13px] font-bold transition-all duration-200"
//             style={{
//               background:  activeTab === t.key ? "#FFFFFF"  : "transparent",
//               color:       activeTab === t.key ? t.accent   : "#94A3B8",
//               boxShadow:   activeTab === t.key ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
//             }}
//           >
//             <Users size={13} />
//             {t.label}
//             <span
//               className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-black"
//               style={{
//                 background: activeTab === t.key ? t.accent + "22" : "#E2E8F0",
//                 color:      activeTab === t.key ? t.accent          : "#64748B",
//               }}
//             >
//               {t.count}
//             </span>
//           </button>
//         ))}
//       </div>

//       {/* ── TAB CONTENT ── */}
//       <TabContent
//         key={activeTab}
//         retailers={activeRetailers}
//         label={activeTab}
//       />
//     </div>
//   );
// }